const getSiteUrl = () => {
  if (typeof window !== 'undefined') {
    if (window.location.hostname.includes('.pk')) return 'https://fluxerp.pk';
    if (window.location.hostname.includes('.qa')) return 'https://fluxerp.qa';
  }
  return 'https://fluxerp.co.uk';
};

const SITE_URL = getSiteUrl();
const DEFAULT_OG_IMAGE = `${SITE_URL}/icons/icon-512x512.png`;

// AEO helper: speakable schema marks sections that voice assistants / AI answer engines
// should read aloud or use as a direct answer snippet.
const speakable = (cssSelectors) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  speakable: { '@type': 'SpeakableSpecification', cssSelector: cssSelectors },
  url: SITE_URL,
});

export const generateHowToSchema = (name, steps) => ({
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name,
  step: steps.map(stepText => ({ '@type': 'HowToStep', text: stepText }))
});

export const generateVideoObjectSchema = (name, description, thumbnailUrl, uploadDate, transcriptUrl) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name,
  description,
  thumbnailUrl,
  uploadDate,
  contentUrl: transcriptUrl
});

export const seoConfig = {
  home: {
    title: 'FluxERP - Cloud ERP & AI-Powered Accounting',
    description: 'The intelligent ERP for modern enterprise operations. IFRS-compliant accounting, manufacturing, supply chain, and AI-powered insights on a sovereign SaaS architecture.',
    keywords: 'cloud ERP, cloud Accounting, SaaS ERP, IFRS accounting software, AI financial insights, manufacturing ERP, supply chain orchestration, multi-entity consolidation, cloud ERP UK',
    canonical: '/',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'FluxERP - Enterprise SaaS ERP Dashboard',
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'FluxERP',
        url: SITE_URL,
        logo: `${SITE_URL}/icons/icon-512x512.png`,
        sameAs: [
          'https://twitter.com/fluxerp',
          'https://www.linkedin.com/company/fluxerp'
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Support',
          email: 'support@fluxerp.co.uk',
          availableLanguage: 'English'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'FluxERP',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        description: 'FluxERP with IFRS-compliant accounting, manufacturing, supply chain, AI insights, and immutable audit governance.',
        url: SITE_URL,
        offers: {
          '@type': 'AggregateOffer',
          lowPrice: '12.99',
          highPrice: '499',
          priceCurrency: 'GBP',
          offerCount: '4'
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          ratingCount: '1250',
          bestRating: '5'
        },
        review: [
          {
            '@type': 'Review',
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
            author: { '@type': 'Person', name: 'Alex Johnson, CFO' },
            reviewBody: 'FluxERP completely automated our IFRS compliance and multi-entity consolidation workflows.'
          },
          {
            '@type': 'Review',
            reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
            author: { '@type': 'Person', name: 'Maria Garcia, Supply Chain Director' },
            reviewBody: 'The real-time warehouse tracking and AI supply chain insights have transformed our operations.'
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'FluxERP',
        url: SITE_URL,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SITE_URL}/faqs?q={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      speakable(['h1', '.hero-description', '.hero-subtitle'])
    ]
  },
  features: {
    title: 'FluxERP Features - IFRS Accounting, Manufacturing & AI | FluxERP',
    description: 'Explore FluxERP enterprise modules: IFRS financials, manufacturing WIP, supply chain orchestration, AI insights, multi-entity consolidation, and immutable audit governance.',
    keywords: 'IFRS compliance software, manufacturing WIP accounting, supply chain ERP, AI financial insights, enterprise audit governance, multi-entity consolidation ERP',
    canonical: '/features',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'FluxERP Features - Enterprise ERP Modules',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'FluxERP Enterprise Modules',
      description: 'Complete list of enterprise ERP modules available in FluxERP',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Enterprise Financials', description: 'IFRS, UK & US GAAP compliant accounting engine' },
        { '@type': 'ListItem', position: 2, name: 'Supply Chain & Warehouse', description: 'Advanced WMS with multi-warehouse zone and bin management' },
        { '@type': 'ListItem', position: 3, name: 'Manufacturing & WIP', description: 'Work-in-Progress accounting and production management' },
        { '@type': 'ListItem', position: 4, name: 'Sales Orchestration', description: 'Omnichannel order fulfillment and POS' },
        { '@type': 'ListItem', position: 5, name: 'AI Financial Insights', description: 'Predictive analytics and anomaly detection' },
        { '@type': 'ListItem', position: 6, name: 'Consolidation & Multi-Entity', description: 'Automated intercompany eliminations and hierarchy reporting' },
        { '@type': 'ListItem', position: 7, name: 'Governance & Audit', description: 'Immutable financial snapshots and enterprise access controls' }
      ]
    }
  },
  pricing: {
    title: 'FluxERP Pricing Plans - Transparent Cloud ERP Pricing | FluxERP',
    description: 'Transparent, scalable pricing for FluxERP. Solo, Team, Growth, and Enterprise plans. All plans include a 14-day free trial. No credit card required.',
    keywords: 'ERP pricing, accounting software pricing, SaaS ERP plans, IFRS compliant ERP cost, FluxERP plans UK',
    canonical: '/pricing',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'FluxERP Pricing Plans',
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'FluxERP',
        description: 'FluxERP with IFRS-compliant accounting, AI insights, and sovereign audit architecture.',
        brand: { '@type': 'Brand', name: 'FluxERP' },
        url: `${SITE_URL}/pricing`,
        offers: [
          {
            '@type': 'Offer',
            name: 'Solo Plan',
            description: 'Smart professional invoicing for freelancers and solo businesses.',
            price: '12.99',
            priceCurrency: 'GBP',
            priceSpecification: { '@type': 'UnitPriceSpecification', price: '12.99', priceCurrency: 'GBP', unitText: 'MONTH' },
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/register`
          },
          {
            '@type': 'Offer',
            name: 'Team Plan',
            description: 'For growing businesses handling daily operations.',
            price: '39',
            priceCurrency: 'GBP',
            priceSpecification: { '@type': 'UnitPriceSpecification', price: '39', priceCurrency: 'GBP', unitText: 'MONTH' },
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/register`
          },
          {
            '@type': 'Offer',
            name: 'Growth Plan',
            description: 'Scale with confidence for multi-branch and scaling businesses.',
            price: '99',
            priceCurrency: 'GBP',
            priceSpecification: { '@type': 'UnitPriceSpecification', price: '99', priceCurrency: 'GBP', unitText: 'MONTH' },
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/register`
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How much does FluxERP cost?',
            acceptedAnswer: { '@type': 'Answer', text: 'FluxERP starts at £12.99/month for the Solo plan. The Team plan is £39/month, Growth is £99/month, and Enterprise pricing is custom. All plans include a 14-day free trial with no credit card required.' }
          },
          {
            '@type': 'Question',
            name: 'Is there a free trial for FluxERP?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. Every FluxERP plan includes a 14-day free trial with full access to all features. No credit card is required to start.' }
          },
          {
            '@type': 'Question',
            name: 'Can I change my FluxERP plan later?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. You can upgrade or downgrade your FluxERP plan at any time from your subscription settings. Changes take effect immediately.' }
          },
          {
            '@type': 'Question',
            name: 'Does FluxERP offer annual billing discounts?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. Quarterly billing saves 5% and annual billing saves up to 17% compared to monthly pricing.' }
          }
        ]
      },
      speakable(['h1', '.pricing-hero-description', '.plan-card'])
    ]
  },
  about: {
    title: 'About FluxERP - Building Sovereign Enterprise ERP Infrastructure',
    description: 'Learn about FluxERP\'s mission to deliver IFRS-compliant, AI-powered SaaS ERP for high-growth global enterprises. Built on the PERN stack with immutable audit architecture.',
    keywords: 'about FluxERP, enterprise ERP company, IFRS accounting software provider, cloud ERP UK, SaaS ERP mission',
    canonical: '/about',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'About FluxERP - Enterprise ERP Company',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'FluxERP',
      url: SITE_URL,
      logo: `${SITE_URL}/icons/icon-512x512.png`,
      description: 'Enterprise SaaS ERP provider delivering IFRS-compliant accounting, manufacturing, and AI-powered insights for high-growth businesses.',
      foundingDate: '2012',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Customer Support',
        email: 'support@fluxerp.co.uk'
      },
      sameAs: [
        'https://twitter.com/fluxerp',
        'https://www.linkedin.com/company/fluxerp'
      ],
      founder: [
        {
          '@type': 'Person',
          name: 'Ahsan Shakeel',
          jobTitle: 'Chief Executive Officer',
          sameAs: 'https://www.linkedin.com/in/ahsan-shakeel'
        },
        {
          '@type': 'Person',
          name: 'Daniyal Khan',
          jobTitle: 'Chief Operating Officer',
          sameAs: 'https://www.linkedin.com/in/daniyal-khan-065876112'
        }
      ]
    }
  },
  contact: {
    title: 'Contact FluxERP - Sales, Support & Partnership Inquiries',
    description: 'Contact FluxERP for sales inquiries, technical support, demo requests, or partnership opportunities. Our team responds within 24 hours.',
    keywords: 'contact FluxERP, ERP sales inquiry, accounting software support, FluxERP UK contact',
    canonical: '/contact',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'Contact FluxERP Support Team',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact FluxERP',
      description: 'Get in touch with FluxERP for sales, support, and partnerships.',
      url: `${SITE_URL}/contact`,
      mainEntity: {
        '@type': 'Organization',
        name: 'FluxERP',
        url: SITE_URL,
        email: 'support@fluxerp.co.uk',
        contactPoint: [
          {
            '@type': 'ContactPoint',
            contactType: 'sales',
            email: 'sales@fluxerp.co.uk',
            availableLanguage: 'English'
          },
          {
            '@type': 'ContactPoint',
            contactType: 'technical support',
            email: 'support@fluxerp.co.uk',
            availableLanguage: 'English'
          }
        ]
      }
    }
  },
  demo: {
    title: 'Request a Demo - See FluxERP Enterprise ERP in Action',
    description: 'Schedule a personalized 30-minute demo of FluxERP. See IFRS-compliant accounting, manufacturing, supply chain, and AI insights tailored to your business needs.',
    keywords: 'FluxERP demo, enterprise ERP demo, IFRS accounting demo, request product demo, ERP product walkthrough',
    canonical: '/demo',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'Request a FluxERP Demo',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: 'FluxERP Product Demo',
      description: 'A personalized 30-minute demo of FluxERP enterprise ERP tailored to your business.',
      eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      organizer: {
        '@type': 'Organization',
        name: 'FluxERP',
        url: SITE_URL
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'GBP',
        availability: 'https://schema.org/InStock',
        url: `${SITE_URL}/demo`
      }
    }
  },

  faqs: {
    title: 'FAQs - Frequently Asked Questions About FluxERP Software',
    description: 'Explore answers about FluxERP cloud ERP software, accounting, AI automation, manufacturing, inventory, POS, payroll, security, pricing, integrations, and business management for companies in the UK, Qatar, Pakistan, and worldwide.',
    keywords: [
      'FluxERP FAQ',
      'ERP software questions',
      'best ERP software',
      'cloud ERP software',
      'accounting ERP software',
      'AI-powered ERP',
      'ERP for SMEs',
      'manufacturing ERP',
      'retail ERP',
      'ERP software UK',
      'ERP software Qatar',
      'ERP software Pakistan',
      'accounting software questions',
      'ERP implementation',
      'ERP integration',
      'ERP security'
    ].join(', '),
    canonical: '/faqs',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'FluxERP ERP Software Frequently Asked Questions',

    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [

          // ==========================================
          // GENERAL ERP & FLUXERP QUESTIONS
          // ==========================================

          {
            '@type': 'Question',
            name: 'What is FluxERP?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is a cloud-based enterprise resource planning (ERP) platform that brings accounting, finance, inventory, purchasing, sales, manufacturing, CRM, HR, payroll, and business operations into one integrated system. It is designed to help SMEs and growing enterprises manage their business processes from a centralized platform.'
            }
          },

          {
            '@type': 'Question',
            name: 'What is ERP software and how does it work?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'ERP software integrates business functions such as accounting, inventory, purchasing, sales, manufacturing, and human resources into a centralized system. FluxERP connects operational transactions with financial records, helping businesses manage workflows, reporting, and business data in one platform.'
            }
          },

          {
            '@type': 'Question',
            name: 'What business problems does FluxERP solve?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP helps businesses reduce disconnected spreadsheets and systems, improve financial visibility, manage inventory, streamline purchasing and sales, coordinate business operations, and centralize reporting. Available capabilities depend on the selected modules and configuration.'
            }
          },

          {
            '@type': 'Question',
            name: 'Who can use FluxERP?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is designed for small and medium-sized businesses, growing enterprises, and organizations that need integrated business management. It supports use cases across trading, wholesale, distribution, manufacturing, retail, FMCG, pharmaceuticals, textiles, restaurants, and other industries where the relevant modules are available.'
            }
          },

          {
            '@type': 'Question',
            name: 'What modules are included in FluxERP?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP provides business management capabilities across accounting and finance, purchasing, sales, inventory, CRM, manufacturing, HR and payroll, POS, reporting, and other operational areas. The exact modules and features available depend on the product configuration and subscription plan.'
            }
          },

          {
            '@type': 'Question',
            name: 'Is FluxERP suitable for growing businesses?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is designed to support growing businesses by integrating financial and operational processes into a centralized ERP platform. Businesses can select relevant modules and configure workflows according to their operational requirements and available product capabilities.'
            }
          },

          {
            '@type': 'Question',
            name: 'What is the difference between FluxERP and traditional ERP software?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is designed as a cloud-based ERP platform with integrated financial and operational modules, configurable workflows, and AI-powered capabilities where available. Businesses should compare ERP platforms based on their required features, implementation needs, integrations, security controls, and total cost of ownership.'
            }
          },

          {
            '@type': 'Question',
            name: 'Is FluxERP a cloud-based ERP system?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. FluxERP is designed as a cloud-based ERP platform that enables businesses to access their configured business management system through supported web interfaces. Availability, infrastructure, and access policies depend on the deployment configuration.'
            }
          },

          // ==========================================
          // ACCOUNTING & FINANCE
          // ==========================================

          {
            '@type': 'Question',
            name: 'Does FluxERP include accounting software?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes. FluxERP includes accounting and financial management capabilities designed to help businesses manage financial transactions, ledgers, reporting, and integrated business processes. Available accounting features depend on the implemented modules and configuration.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP support double-entry accounting?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is designed around integrated double-entry accounting and financial transaction recording. The accounting system connects relevant operational transactions with financial records according to configured posting rules and workflows.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP support IFRS accounting?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP supports accounting capabilities designed to help businesses work with IFRS-related financial requirements. Specific IFRS reporting, accounting treatments, and compliance outcomes depend on the implemented features, configuration, and professional accounting review.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP support UK GAAP and US GAAP?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP can be configured for relevant accounting requirements where the applicable functionality is implemented. UK GAAP and US GAAP support must be evaluated against the required accounting treatments, reporting rules, and current product capabilities.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP manage accounts payable and accounts receivable?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP supports business processes related to accounts payable and accounts receivable where the relevant accounting and transaction modules are enabled. Businesses can use integrated financial workflows to manage applicable supplier and customer transactions.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP support financial reporting?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP provides financial reporting capabilities through its accounting and business management modules. Available reports depend on the configured chart of accounts, transaction data, reporting features, and user permissions.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP support multi-currency accounting?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP supports multi-currency functionality where configured, including currency-based transactions and financial reporting. Exchange rates, foreign exchange revaluation, and other currency accounting capabilities should be verified against the available implementation.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP manage budgets and financial performance?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP includes financial management capabilities that can support budgeting and performance analysis where the relevant modules are implemented. Available budget controls, variance reporting, and forecasting features depend on the product configuration.'
            }
          },

          // ==========================================
          // AI & AUTOMATION
          // ==========================================

          {
            '@type': 'Question',
            name: 'Does FluxERP use artificial intelligence?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP includes AI-powered business assistance capabilities designed to help users interact with business information, understand workflows, and access relevant insights. AI functionality depends on the implemented features and available configuration.'
            }
          },

          {
            '@type': 'Question',
            name: 'What can the FluxERP AI assistant do?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The FluxERP AI assistant is designed to help users navigate supported business modules, understand system workflows, and interact with available business information. Specific tasks, supported languages, and access to financial or operational data depend on the implemented AI capabilities and user permissions.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP AI help identify overdue invoices?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Where the relevant AI assistant and financial data access are enabled, FluxERP can support users in reviewing business information such as overdue invoices. Results depend on available accounting data, permissions, and the implemented AI functionality.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP AI help with inventory and product reordering?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP provides inventory management capabilities, and supported AI functionality may assist users in reviewing inventory-related information. Reorder recommendations, alerts, and automated purchasing actions depend on the implemented features and configuration.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can users interact with the FluxERP AI assistant in different languages?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is being designed to support multilingual AI-assisted interactions, including English, Arabic, and Urdu. Supported languages, features, and accuracy should be confirmed against the current AI assistant implementation.'
            }
          },

          // ==========================================
          // INDUSTRY-SPECIFIC ERP
          // ==========================================

          {
            '@type': 'Question',
            name: 'Does FluxERP provide manufacturing ERP software?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP provides manufacturing-related ERP capabilities where the relevant modules are implemented, including supported production and inventory workflows. Manufacturing requirements should be evaluated against the available bill of materials, work-in-progress, production, costing, and reporting features.'
            }
          },

          {
            '@type': 'Question',
            name: 'Is FluxERP suitable for trading and wholesale businesses?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is designed to support trading, wholesale, and distribution business processes through relevant sales, purchasing, inventory, accounting, and operational modules. The appropriate configuration depends on the business model and required workflows.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP be used by FMCG distributors?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP can support FMCG distribution workflows where the required inventory, purchasing, sales, customer, and accounting features are available. Businesses should evaluate the system against their specific distribution, stock control, and reporting requirements.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP support pharmaceutical businesses?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP can support relevant pharmaceutical trading or manufacturing workflows where the required features are implemented. Businesses should verify product traceability, batch management, expiry tracking, regulatory reporting, and other pharmaceutical-specific requirements before deployment.'
            }
          },

          {
            '@type': 'Question',
            name: 'Is FluxERP suitable for retail stores and supermarkets?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP provides retail and point-of-sale capabilities where implemented, including supported sales, inventory, and accounting workflows. Retail businesses should evaluate the available POS, stock management, reporting, and integration features against their requirements.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP support textile manufacturing?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP can support relevant textile manufacturing processes where the required manufacturing, inventory, costing, and accounting features are implemented. Textile businesses should assess the platform against their production and supply chain requirements.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP offer HR and payroll management?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP provides HR and payroll management capabilities where the relevant modules are enabled. Supported features, payroll calculations, employee records, and statutory requirements depend on the implementation and applicable country configuration.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP support construction companies?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP may support construction-related business management requirements through available accounting, procurement, inventory, project, and operational features. Construction companies should verify the implemented project costing, contract management, and industry-specific workflows before adoption.'
            }
          },

          // ==========================================
          // POS, INVENTORY & OPERATIONS
          // ==========================================

          {
            '@type': 'Question',
            name: 'Does FluxERP include a point-of-sale (POS) system?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, FluxERP includes POS functionality where the POS module is enabled. Supported capabilities include sales transactions and relevant payment and accounting workflows, subject to the current implementation and configuration.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP manage inventory across multiple locations?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP provides inventory management capabilities, including supported multi-location workflows where implemented. Businesses should verify warehouse structures, stock transfers, and location-level inventory reporting against their operational requirements.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP support purchase orders and supplier management?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP supports purchasing and supplier-related business processes where the relevant modules are enabled. Available workflows may include purchase requests, purchase orders, supplier transactions, and related accounting operations.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP manage sales, invoices, and customer accounts?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP provides sales and customer-related business management capabilities. Available functionality includes supported sales transactions, invoicing, customer records, and integrated accounting workflows, depending on the configured modules.'
            }
          },

          // ==========================================
          // MULTI-COMPANY & MULTI-TENANT
          // ==========================================

          {
            '@type': 'Question',
            name: 'Does FluxERP support multi-company management?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Yes, FluxERP is designed to support multi-company management with company-specific configuration and data separation. The available company limits, intercompany functionality, and consolidated reporting depend on the subscription plan and implemented features.'
            }
          },

          {
            '@type': 'Question',
            name: 'What is multi-tenant ERP software?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Multi-tenant ERP software allows multiple organizations or tenants to use a shared application infrastructure while maintaining logical separation of their data and configuration. FluxERP is designed as a multi-tenant cloud ERP platform with tenant-level access controls and data isolation.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can I manage multiple businesses in one FluxERP account?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP supports multi-company functionality where enabled, allowing organizations to manage multiple business entities within the configured platform. Company access, accounting configuration, and reporting depend on user permissions and the selected plan.'
            }
          },

          // ==========================================
          // SECURITY & ACCESS CONTROL
          // ==========================================

          {
            '@type': 'Question',
            name: 'How secure is FluxERP?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP uses application-level security controls such as authentication, authorization, and access management. Specific encryption, MFA, audit logging, infrastructure protection, and other security controls should be evaluated against the current implementation and deployment configuration.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP support role-based access control?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP supports role-based access control where implemented, allowing organizations to manage user permissions according to their roles and responsibilities. Available permissions depend on the configured modules and authorization policies.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can users log in to FluxERP from multiple devices?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP authentication supports device session management according to the configured session policy. When an active session is detected on another device, the system can require explicit confirmation before replacing the existing session, where this functionality is implemented.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP maintain audit trails?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP includes audit-related capabilities for supported business and financial workflows. The exact records, immutability controls, retention policies, and audit coverage depend on the implemented modules and system configuration.'
            }
          },

          // ==========================================
          // API & INTEGRATIONS
          // ==========================================

          {
            '@type': 'Question',
            name: 'Does FluxERP provide API access?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP provides API integration capabilities where enabled. Available API access, endpoints, authentication methods, rate limits, and subscription requirements depend on the current API implementation and plan.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP integrate with existing business software?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP can support integrations through available APIs and integration capabilities. Compatibility depends on the external application, available endpoints, authentication requirements, data formats, and the scope of the integration.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP be customized for my business?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP provides configurable business management capabilities, and additional customization or integration may be available depending on the business requirements and implementation scope. Customization options should be discussed with the FluxERP team.'
            }
          },

          // ==========================================
          // PRICING & IMPLEMENTATION
          // ==========================================

          {
            '@type': 'Question',
            name: 'Does FluxERP offer a free trial?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP offers a 14-day free trial where available. Trial access, included features, and eligibility should be confirmed through the current pricing and signup process.'
            }
          },

          {
            '@type': 'Question',
            name: 'How much does FluxERP cost?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP pricing depends on the selected subscription plan, business requirements, region, and included features. Visit the FluxERP pricing page or contact the sales team for current pricing and available plans.'
            }
          },

          {
            '@type': 'Question',
            name: 'Does FluxERP offer ERP software for small businesses?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is designed to support SMEs and growing businesses with integrated accounting and business management capabilities. Available plans and modules allow businesses to evaluate the functionality relevant to their size and operational requirements.'
            }
          },

          {
            '@type': 'Question',
            name: 'How long does FluxERP implementation take?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP implementation time depends on the business size, selected modules, data migration requirements, integrations, configuration, and user training. A suitable implementation timeline can be determined after reviewing the organizations requirements.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can I migrate my data from another ERP system to FluxERP?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Data migration to FluxERP may be supported depending on the source system, available import capabilities, data structure, and migration requirements. The migration process should be planned to validate data quality, opening balances, historical transactions, and relevant master records.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can I cancel my FluxERP subscription?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Subscription cancellation is subject to the applicable FluxERP subscription terms. Contact the FluxERP team or review the current terms for cancellation procedures, data export options, and retention policies.'
            }
          },

          // ==========================================
          // REGIONAL SEO QUESTIONS
          // ==========================================

          {
            '@type': 'Question',
            name: 'Is FluxERP available in the UK?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is marketed for businesses in the United Kingdom and other target markets. UK businesses can evaluate its accounting, ERP, and operational capabilities against their business requirements and applicable accounting and compliance obligations.'
            }
          },

          {
            '@type': 'Question',
            name: 'Is FluxERP available in Qatar?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is marketed for businesses in Qatar, including organizations seeking integrated accounting and business management software. Businesses should confirm regional configuration, tax requirements, implementation support, and available features before deployment.'
            }
          },

          {
            '@type': 'Question',
            name: 'Is FluxERP available in Pakistan?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is marketed for businesses in Pakistan, including SMEs and growing enterprises. Available accounting, inventory, sales, manufacturing, and other ERP features depend on the selected configuration and applicable local business requirements.'
            }
          },

          {
            '@type': 'Question',
            name: 'Can FluxERP support businesses operating in multiple countries?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'FluxERP is designed to support businesses operating across different markets through relevant company, accounting, and currency management capabilities. Country-specific tax, compliance, and reporting requirements should be assessed against the implemented functionality for each jurisdiction.'
            }
          },

          {
            '@type': 'Question',
            name: 'How can I contact the FluxERP team?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'You can contact the FluxERP team through the official FluxERP website to request product information, discuss implementation requirements, ask about pricing, or arrange a product demonstration.'
            }
          }

        ]
      },

      speakable(['h1', '.faq-question', '.faq-answer'])
    ]
  },
  security: {
    title: 'Security & Governance - Immutable Financial Infrastructure | FluxERP',
    description: 'FluxERP sovereign security: immutable audit snapshots, MFA enforcement, IFRS compliance, AES-256 encryption, tenant isolation, and AI-powered threat detection.',
    keywords: 'ERP security, immutable audit trail, financial governance, enterprise security controls, IFRS compliance software, GDPR compliant ERP, secure SaaS ERP',
    canonical: '/security',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'FluxERP Security and Governance',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'FluxERP Security & Governance',
      description: 'Enterprise-grade security with immutable snapshots, MFA, IFRS compliance, and AI-powered threat detection.',
      url: `${SITE_URL}/security`,
      publisher: { '@type': 'Organization', name: 'FluxERP', url: SITE_URL }
    }
  },
  partner: {
    title: 'Become a FluxERP Partner - Reseller & Referral Program',
    description: 'Join the FluxERP partner program. Earn competitive commissions, access co-marketing materials, get dedicated support, and grow your business with our enterprise ERP platform.',
    keywords: 'FluxERP partner program, ERP reseller, accounting software referral, VAR program, FluxERP commissions, become a partner',
    canonical: '/partner',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'Become a FluxERP Partner',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'FluxERP Partner Program',
      description: 'Join the FluxERP partner ecosystem to earn commissions, access co-marketing resources, and grow your business.',
      url: `${SITE_URL}/partner`,
      publisher: { '@type': 'Organization', name: 'FluxERP', url: SITE_URL }
    }
  },
  privacy: {
    title: 'Privacy Policy - Data Protection & GDPR Compliance | FluxERP',
    description: 'FluxERP privacy policy. Learn how we collect, use, store, and protect your personal and business data in accordance with GDPR and UK data protection laws.',
    keywords: 'FluxERP privacy policy, GDPR compliance, data protection, personal data handling',
    canonical: '/privacy',
    noindex: false
  },
  terms: {
    title: 'Terms of Service - Legal Terms & Conditions | FluxERP',
    description: 'FluxERP terms of service. Read the legal terms and conditions governing use of our cloud ERP and accounting software.',
    keywords: 'FluxERP terms of service, ERP legal terms, SaaS user agreement',
    canonical: '/terms',
    noindex: false
  },
  'cookie-policy': {
    title: 'Cookie Policy | FluxERP',
    description: 'FluxERP cookie policy. Learn which cookies we use, why we use them, and how to manage your cookie preferences.',
    keywords: 'FluxERP cookie policy, website cookies, cookie consent',
    canonical: '/cookie-policy',
    noindex: false
  },
  dpa: {
    title: 'Data Processing Agreement (DPA) | FluxERP',
    description: 'FluxERP Data Processing Agreement (DPA) for GDPR compliance. Details of how we process customer data as a data processor.',
    keywords: 'FluxERP DPA, data processing agreement, GDPR data processor',
    canonical: '/dpa',
    noindex: false
  },
  login: {
    title: 'Sign In - Access Your FluxERP Account',
    description: 'Sign in to your FluxERP account to access your enterprise ERP dashboard.',
    canonical: '/login',
    noindex: true
  },
  register: {
    title: 'Start Free Trial - Create Your FluxERP Account',
    description: 'Create your FluxERP account and start a 14-day free trial. Full access to all enterprise ERP features. No credit card required.',
    keywords: 'FluxERP free trial, create account, ERP sign up, 14-day trial',
    canonical: '/register',
    noindex: true
  }
};

export const getPageSEO = (page) => seoConfig[page] || seoConfig.home;
